import { Close as CloseIcon, History as HistoryIcon } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';
import { Palette } from 'Palette';
import { VersionHistorySidebarProps } from './fullContentDiffViewerTypes';
import {
  formatVersionDate,
  formatVersionTime,
  getAssociatedModelDisplay,
  getEventTypeLabel,
  getInitiatorDisplayName,
} from './fullContentDiffViewerUtils';
import { useVersionHistory } from './useVersionHistory';

const VersionHistorySidebar: React.FC<VersionHistorySidebarProps> = ({
  isOpen,
  onClose,
  modelType,
  modelId,
  onVersionSelect,
  onVersionsLoaded,
  selectedVersionId,
  tone = 'default',
}) => {
  const { versions, loading, error } = useVersionHistory({
    isOpen,
    modelType,
    modelId,
    onVersionsLoaded,
    selectedVersionId,
    onVersionSelect,
  });

  return (
    <Drawer
      anchor="left"
      open={isOpen}
      onClose={onClose}
      variant="persistent"
      ModalProps={{
        disableScrollLock: true,
        hideBackdrop: true,
      }}
      sx={{
        '& .MuiDrawer-paper': {
          width: {
            md: '25vw',
            lg: '20vw',
            xl: '15vw',
            '@media (max-width: 1744px)': '12vw',
          },
          minWidth: '250px',
          maxWidth: '400px',
          backgroundColor: Palette[tone].background,
          borderRight: `1px solid ${Palette[tone].border}`,
          position: 'fixed',
          zIndex: 1200,
        },
      }}
    >
      <Box sx={{ p: 2, borderBottom: `1px solid ${Palette[tone].border}` }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon sx={{ color: Palette[tone].text }} />
            <Typography variant="h6" sx={{ color: Palette[tone].text }}>
              Version History
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2 }}>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Box>
        ) : versions.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography color="text.secondary" variant="body2">
              No version history found
            </Typography>
          </Box>
        ) : (
          <List>
            {versions.map((version, index) => {
              const isSelected = version.id === selectedVersionId;
              return (
                <React.Fragment key={version.id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => onVersionSelect(version)}
                      selected={isSelected}
                      sx={{
                        borderLeft: isSelected
                          ? `4px solid ${Palette[tone].text}`
                          : '4px solid transparent',
                        backgroundColor: isSelected ? Palette[tone].activeBg : 'transparent',
                        '&:hover': {
                          backgroundColor: isSelected
                            ? Palette[tone].activeBg
                            : Palette[tone].iconCircle,
                        },
                        '&.Mui-selected': {
                          backgroundColor: Palette[tone].activeBg,
                        },
                        py: 2,
                      }}
                    >
                      <ListItemText
                        primary={
                          <Tooltip
                            title={
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                  {getEventTypeLabel(version.eventType)} -{' '}
                                  {formatVersionDate(version.eventTimestamp)} at{' '}
                                  {formatVersionTime(version.eventTimestamp)}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  {getInitiatorDisplayName(version)}
                                </Typography>
                                <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                                  Model: {getAssociatedModelDisplay(version)}
                                </Typography>
                              </Box>
                            }
                            placement="right"
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: isSelected ? Palette[tone].activeText : Palette[tone].text,
                                fontWeight: isSelected ? 600 : 400,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {formatVersionDate(version.eventTimestamp)}
                            </Typography>
                          </Tooltip>
                        }
                        secondary={null}
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < versions.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Box>
    </Drawer>
  );
};

export default VersionHistorySidebar;
